/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { routes, routeHref } from '@/route-paths';
import { readSeoUrl } from '@/lib/product/seo-url';

/**
 * RGBARRY-CUSTOMIZATION: production-identical category menu links (W-23875749; follow-up to
 * W-23754187).
 *
 * Production baggallini.com serves category pages at platform SEO slug paths (`/crossbody-handbags`,
 * `/anti-theft/shop-anti-theft`), while Storefront Next's default category route is the id-based
 * `/category/:categoryId`. The `int_urlresolver_scapi` endpoint resolves each category id to its
 * canonical slug path using the same URL-Rules engine production uses; the layout loader batches all
 * mega-menu category ids into one call and passes the resulting `categoryId -> slugPath` map down
 * through this context.
 *
 * `useCategoryHref()` returns a resolver that prefers the resolved slug path and falls back to the
 * id-based route when a category is unresolved (endpoint miss, resolver outage, or a category not in
 * the batch). Slug navigations are handled by the legacy-URL catch-all route (see
 * `lib/api/url-mapping.server.ts`), so a resolved link lands on the same page the id-based link would.
 */
export type CategoryUrlMap = Record<string, string>;
export type CategoryHrefTarget = string | { id: string; c_seoUrl?: unknown };

const CategoryHrefContext = createContext<CategoryUrlMap>({});

export function CategoryHrefProvider({ value, children }: { value: CategoryUrlMap; children: ReactNode }): ReactNode {
    return <CategoryHrefContext value={value}>{children}</CategoryHrefContext>;
}

/**
 * Returns a stable function mapping a category id or enriched category node to its href. A node's
 * hook-injected URL wins so deferred menu categories do not depend on the root-only URL map.
 */
// oxlint-disable-next-line react-refresh/only-export-components
export function useCategoryHref(): (target: CategoryHrefTarget) => string {
    const map = useContext(CategoryHrefContext);
    return useMemo(
        () => (target: CategoryHrefTarget) => {
            const categoryId = typeof target === 'string' ? target : target.id;
            return (
                (typeof target === 'string' ? undefined : readSeoUrl(target)) ??
                map[categoryId] ??
                routeHref(routes.category, { categoryId })
            );
        },
        [map]
    );
}

/**
 * Resolves Page Designer's authored `/category/:id` URLs through the same canonical category map
 * used by navigation. Other strings and unresolved category ids are intentionally left unchanged.
 */
// oxlint-disable-next-line react-refresh/only-export-components
export function useCanonicalCategoryTarget(target: string | undefined): string | undefined {
    const map = useContext(CategoryHrefContext);

    return useMemo(() => {
        if (!target) return target;

        const match = /^\/category\/([^/?#]+)\/?([?#].*)?$/.exec(target);
        if (!match) return target;

        let categoryId: string;
        try {
            categoryId = decodeURIComponent(match[1]);
        } catch {
            return target;
        }

        const seoUrl = readSeoUrl({ c_seoUrl: map[categoryId] });
        return seoUrl ? `${seoUrl}${match[2] ?? ''}` : target;
    }, [map, target]);
}
